import { Helmet } from 'react-helmet-async';
import { seoConfig } from '../config/seo';

interface ArticleMeta {
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  tags?: string[];
  section?: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  ogImageAlt?: string;
  article?: ArticleMeta;
  schema?: object | object[];
  noIndex?: boolean;
}

export function SEO({
  title,
  description = seoConfig.defaultDescription,
  canonical,
  ogType = 'website',
  ogImage = seoConfig.defaultImage,
  ogImageAlt,
  article,
  schema,
  noIndex = false,
}: SEOProps) {
  const fullTitle = title
    ? `${title} | ${seoConfig.siteName}`
    : seoConfig.defaultTitle;

  const canonicalUrl = canonical
    ? `${seoConfig.siteUrl}${canonical}`
    : undefined;

  const jsonLdSchemas = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* hreflang tags for multilingual content */}
        {canonicalUrl && (
          <>
            <link rel="alternate" hrefLang="en" href={canonicalUrl} />
            <link rel="alternate" hrefLang="zh" href={canonicalUrl} />
            <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
          </>
        )}

        <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />

        <meta property="og:site_name" content={seoConfig.siteName} />
        <meta property="og:type" content={ogType} />
        <meta property="og:title" content={title || seoConfig.defaultTitle} />
        <meta property="og:description" content={description} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content={ogImage} />
        {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="zh_CN" />

        {ogType === 'article' && article?.publishedTime && (
          <meta property="article:published_time" content={article.publishedTime} />
        )}
        {ogType === 'article' && article?.modifiedTime && (
          <meta property="article:modified_time" content={article.modifiedTime} />
        )}
        {ogType === 'article' && article?.author && (
          <meta property="article:author" content={article.author} />
        )}
        {ogType === 'article' && article?.section && (
          <meta property="article:section" content={article.section} />
        )}
        {ogType === 'article' &&
          article?.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || seoConfig.defaultTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* JSON-LD rendered outside Helmet for proper SSR/pre-rendering support */}
      {jsonLdSchemas.map((schemaItem, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaItem) }}
        />
      ))}
    </>
  );
}
